<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">{{title}}</h1>

                    <a :href="`/#/admin/organizations/workspaces/${organizationUnitId}`" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        Back
                    </a>

                    <a :href="`/#/admin/organizations/workspaces/edit/${id}`" class="btn btn-link btn-sm">
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        Edit
                    </a>
                </div>
                <div class="col-lg-5 text-right">
                    <FileUploader :ttl="60" :skipConfirmation="true" :hideProgress="true" @selected="file=$event" @uploaded="handleFileUploaded">
                        <button type="button" 
                                :disabled="invokingImportToWorkspaceCommandV1"
                                class="btn btn-success">Upload portfolio</button>
                    </FileUploader>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div v-if="usersInWorkspace.length" style="margin-bottom: 50px;">
                <h4 class="fg-color-blue">Members</h4>
                <br />

                <table class="table table-striped table-hover">
                    <tbody>
                        <tr v-for="u in usersInWorkspace">
                            <td>{{u.DisplayName}}</td>
                            <td>{{u.Email}}</td>
                            <td>{{u.IsExternal ? 'External' : 'Organization'}}</td>
                            <td>
                                <button @click="handleRemoveUserFromWorkspaceCommandV1(u)" class="btn btn-secondary btn-sm pull-right">
                                    Remove an access
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style="margin-bottom: 40px;">
                <h4 class="fg-color-blue">Invite people from outside the organization</h4>

                <ul v-if="invitations.length">
                    <li v-for="email in invitations" :key="email">Pending invitation for {{email}}</li>
                </ul>

                <div class="input-group mb-3">
                    <input v-model.trim="inputRecipientEmail" type="text" class="form-control" placeholder="By email">
                    <div class="input-group-append">
                        <button @click="handleInviteToWorkspaceCommandV1" class="btn btn-primary" type="button">Invite</button>
                    </div>
                </div>
                <br />

                <div class="row">
                    <div v-for="u in usersNotInWorkspace" class="col-sm-6" style="margin-bottom: 20px;">
                        <div class="card bg-light">
                            <div class="card-header">
                                Organization user
                                <button @click="handleAddUserToWorkspaceCommandV1(u)"
                                        class="btn btn-primary btn-sm pull-right">
                                    Add <i class="fa fa-plus" aria-hidden="true"></i>
                                </button>
                            </div>
                            <div class="card-body">
                                <h5 class="card-title">{{u.DisplayName}}</h5>
                                <p class="card-text"><em>{{u.Email}}</em></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute } from 'vue-router'
    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'
    import FileUploader from '@Blobs/Components/FileUploader.vue'

    import
        {
            useWorkspaceMembersQueryV1
        } from '@Organizations/Workspaces/queries-vue-composables'

    import
        {
            useAddUserToWorkspaceCommandV1,
            useInviteToWorkspaceCommandV1,
            useRemoveUserFromWorkspaceCommandV1,
            useImportToWorkspaceCommandV1
        } from '@Organizations/Workspaces/commands-vue-composables'

    // ---------------- route / base ----------------

    const route = useRoute()
    const id = computed(() => String(route.params.id))

    // ---------------- local state ----------------

    const file = ref<File | null>(null)
    const inputRecipientEmail = ref<string | null>(null)
    const userId = ref<any | null>(null)
    const blobId = ref<string | null>(null)

    // ---------------- queries ----------------


    const {
        runWorkspaceMembersQueryV1,
        modelWorkspaceMembersQueryV1: model
    } = useWorkspaceMembersQueryV1(id)


    // ---------------- commands ----------------

    const { invokeAddUserToWorkspaceCommandV1,
        onSucceedAddUserToWorkspaceCommandV1        
    } = useAddUserToWorkspaceCommandV1(id, userId)

    const { invokeRemoveUserFromWorkspaceCommandV1,
        onSucceedRemoveUserFromWorkspaceCommandV1
    } = useRemoveUserFromWorkspaceCommandV1(id, userId)

    const { 
        invokeInviteToWorkspaceCommandV1,
        onSucceedInviteToWorkspaceCommandV1
    } = useInviteToWorkspaceCommandV1(id, inputRecipientEmail)

    const {
        invokeImportToWorkspaceCommandV1,
        invokingImportToWorkspaceCommandV1,
        succeedImportToWorkspaceCommandV1
    } = useImportToWorkspaceCommandV1(id, blobId)

    // ---------------- derived ----------------

    const organizationUnitId = computed(() => model.value?.OrganizationUnitId)
    const workspaceName = computed(() => model.value?.WorkspaceName)
    const usersInWorkspace = computed(() => model.value?.UsersInWorkspace ?? [])
    const usersNotInWorkspace = computed(() => model.value?.UsersNotInWorkspace ?? [])
    const invitations = computed(() => model.value?.Invitations ?? [])
    const title = computed(() => `Manage ${workspaceName.value || 'workspace'}`)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        runWorkspaceMembersQueryV1()
    })

    watch(title, newTitle =>
    {
        document.title = newTitle
    })

    // ---------------- command side-effects ----------------

    function refreshMembers()
    {
        runWorkspaceMembersQueryV1()
    }

    onSucceedAddUserToWorkspaceCommandV1(refreshMembers)
    onSucceedInviteToWorkspaceCommandV1(refreshMembers)
    onSucceedRemoveUserFromWorkspaceCommandV1(refreshMembers)

    watch(succeedImportToWorkspaceCommandV1, succeed =>
    {
        if (succeed === true)
        {
            alert(`Portfolio imported successfully into '${workspaceName.value}'`)
        }
        else if (succeed === false)
        {
            alert('Import failed')
        }
    })

    // ---------------- handlers ----------------

    function handleAddUserToWorkspaceCommandV1(user: any)
    {
        userId.value = user.UserId;
        invokeAddUserToWorkspaceCommandV1()
        userId.value = null;
    }

    function handleRemoveUserFromWorkspaceCommandV1(user: any)
    {
        if (confirm(`Do you want to remove user ${user.Email} from workspace?`))
        {
            invokeRemoveUserFromWorkspaceCommandV1({ userId: user.UserId })
        }
    }

    function handleInviteToWorkspaceCommandV1()
    {
        if (!inputRecipientEmail.value) return

        invokeInviteToWorkspaceCommandV1()
        inputRecipientEmail.value = null
    }

    function handleFileUploaded(bId: string)
    {
        if (!file.value) return

        if (!confirm(`Do you really want to import '${file.value.name}' into '${workspaceName.value}' workspace?`))
            return


        blobId.value = bId;
        invokeImportToWorkspaceCommandV1()
        blobId.value = null
    }
</script>